<template>
  <ResourceCombobox
    :model-value="modelValue"
    :items="options"
    :loading="loading"
    :has-more="hasMore"
    :placeholder="placeholder || t('customers.pickPlaceholder')"
    :button-class="buttonClass"
    @update:model-value="onSelect"
    @search="onSearch"
    @load-more="onLoadMore"
    @open-change="onOpenChange"
  >
    <template #selected="{ item }">
      <span v-if="item.raw" class="min-w-0 truncate">
        <span class="truncate font-medium">{{ customerOf(item.raw) }}</span>
        <span class="ml-1.5 truncate text-muted-foreground">{{ item.raw.attributes.display_name }}</span>
      </span>
      <span v-else class="truncate">{{ item.label }}</span>
    </template>

    <!-- The customer NAMES the row and the address identifies it: two Tremblays
         are two customers, and one customer can hold several addresses, so
         neither line alone can be picked from with confidence. -->
    <template #item="{ item }">
      <span v-if="item.raw" class="flex min-w-0 items-center gap-2.5">
        <CustomerAvatar :name="customerOf(item.raw)" :identity="identityOf(item.raw)" class="shrink-0" />
        <span class="flex min-w-0 flex-col">
          <span class="truncate font-medium">{{ customerOf(item.raw) }}</span>
          <span class="truncate text-xs text-muted-foreground">{{ item.raw.attributes.display_name }}</span>
        </span>
      </span>
    </template>

    <!-- « + Nouvelle adresse » only names the intent and hands the typed term
         up: a new customer needs an owner plus an address, which is a form, not
         a popover footer. The create block lives in the caller (Slice 3). -->
    <template v-if="allowCreate" #footer="{ searchTerm, close }">
      <button
        type="button"
        class="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm hover:bg-sunken"
        data-testid="customer-create-row"
        @click="startCreate(searchTerm, close)"
      >
        <Plus class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span class="min-w-0 flex-1 truncate">{{ t('customers.newAddress') }}</span>
      </button>
    </template>
  </ResourceCombobox>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Plus } from 'lucide-vue-next'
import { useServiceLocations } from '@/composables/useServiceLocations'
import ResourceCombobox from './ResourceCombobox.vue'
import CustomerAvatar from './CustomerAvatar.vue'
import { customerKey } from '@/lib/customer'
import type { Resource } from '@/api/resources'

/**
 * THE intake customer control: pick the ADDRESS, see who is there.
 *
 * The location is the durable anchor — customers move, the hedge stays — so the
 * thing being picked is the place, searched by its customer's name or by its
 * address. `with_customer` is what keeps an ownerless legacy row out of intake
 * — the address book lists such a row; this picker never does.
 *
 * ⚠ `customer_label` is SERVED (company-first, agreeing with the job's
 * `customer_name`) — never recompose it here, or the picker and the job it
 * creates will disagree about what the customer is called.
 */
const props = defineProps<{
  modelValue?: string | null
  placeholder?: string
  buttonClass?: string
  // Offers « + Nouvelle adresse ». On for the intake forms (the phone is
  // ringing); off for filters, where minting a customer mid-pick is nonsense.
  allowCreate?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  // The picked resource, not just its id — callers need the customer's name for
  // the job title and the location for the lead guard, and would otherwise
  // re-fetch a record this component already holds.
  'select': [location: Resource<'service_location'> | null]
  // « + Nouvelle adresse » — the typed term, for the caller's own create block.
  'new-address': [term: string]
}>()

const { t } = useI18n()
const {
  serviceLocations, loading, hasMore,
  searchByCustomer, loadMoreServiceLocations, fetchServiceLocation,
} = useServiceLocations()

// A handed-in id (an intake prefill) must label the trigger immediately, and
// must survive a later list-replacing fetch — without this a preselected
// customer reads as an empty field until the dropdown is opened.
const hydrated = ref<Resource<'service_location'>[]>([])

const known = computed(() => {
  const map = new Map<string, Resource<'service_location'>>()
  for (const l of hydrated.value) map.set(l.id, l)
  for (const l of serviceLocations.value) map.set(l.id, l)
  return map
})

// The customer, else the address — a location with no owner cannot be reached
// by search, but one can still arrive here as a prefilled id.
const customerOf = (location: Resource<'service_location'>): string =>
  location.attributes.customer_label || location.attributes.display_name

// The disc hashes on the customer, so she wears the same colour here as on the
// sales desk. Read from the ADDRESS's current owner — the right slots for a
// picker, which answers « who is there now », not a past job's frozen stamp.
// An ownerless row (a prefill only) hashes on itself.
const identityOf = (location: Resource<'service_location'>): string =>
  customerKey(location.relationships?.contact?.data?.id, location.relationships?.company?.data?.id)
    ?? location.id

const options = computed(() =>
  [...known.value.values()].map(l => ({
    value: l.id,
    label: `${customerOf(l)} — ${l.attributes.display_name}`,
    raw: l,
  }))
)

watch(() => props.modelValue, async (id) => {
  if (!id || known.value.has(id)) return
  const found = await fetchServiceLocation(id)
  if (found && !hydrated.value.some(h => h.id === found.id)) hydrated.value.push(found)
}, { immediate: true })

// A pick is pinned like a prefill: closing the popover resets the search to
// page 1, and a row picked from a later page (or from a typed search) would
// otherwise drop out of the list and blank the trigger.
const onSelect = (id: string | null) => {
  const location = id ? known.value.get(id) ?? null : null
  if (location && !hydrated.value.some(h => h.id === location.id)) hydrated.value.push(location)
  emit('update:modelValue', id)
  emit('select', location)
}

const startCreate = (term: string, close: () => void) => {
  emit('new-address', term.trim())
  close()
}

const onSearch = async (term: string) => {
  await searchByCustomer(term)
}

const onLoadMore = async () => {
  await loadMoreServiceLocations()
}

const onOpenChange = async (open: boolean) => {
  if (open && serviceLocations.value.length === 0) await searchByCustomer()
}
</script>
